import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, ShieldCheck, Lock, Trophy, Target, ChevronRight, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1696542095242-53305aaa6d9c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxhbWVyaWNhbiUyMGZvb3RiYWxsJTIwc3RhZGl1bSUyMG5pZ2h0JTIwbGlnaHRzfGVufDB8fHx8MTc4Njk3MTY2MHww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Target, title: "Single-Tap Picks", desc: "Pick weekly winners with one tap. Cards lock automatically at kickoff." },
  { icon: Trophy, title: "Live Leaderboard", desc: "Track your season points and rank against the league in real time." },
  { icon: Zap, title: "Escalating Scoring", desc: "Points rise as the season heats up, with late-season catch-up rules." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/access-requests", form);
      toast.success(data.message || "Request submitted!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] grain text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-[#013369] flex items-center justify-center">
              <Zap className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-xl font-extrabold tracking-tight">NFL Pick <span className="neon-text">House</span></span>
          </div>
          <button data-testid="landing-signin-btn" onClick={() => navigate("/login")}
            className="px-4 py-2 rounded-full bg-[#D50A0A] text-white text-sm font-bold hover:shadow-[0_0_15px_rgba(213,10,10,0.4)] active:scale-95 transition-transform">
            Sign in
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-24 sm:py-36">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="overline">Private NFL Pick 'Em League</span>
            <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-4 leading-[1.05]">
              Outpick the league.<br /><span className="neon-text">Every single week.</span>
            </h1>
            <p className="text-zinc-300 text-lg mt-6 leading-relaxed max-w-xl">
              NFL Pick House is a high-stakes NFL Pick 'Em experience powered by live Tank01 data —
              real schedules, live scores, and automatic kickoff lockouts. Invite-only.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="#request" data-testid="hero-request-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D50A0A] text-white font-bold hover:shadow-[0_0_20px_rgba(213,10,10,0.45)] active:scale-95 transition-transform">
                Request Access <ChevronRight className="h-4 w-4" />
              </a>
              <button onClick={() => navigate("/login")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 font-semibold hover:border-[#4C8DFF] hover:text-[#4C8DFF] transition-colors">
                I have an account
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] p-7 hover:border-[#27272A] hover:-translate-y-1 transition-transform">
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center mb-5 ${i === 1 ? "bg-[#1D4ED8]/15" : "bg-[#D50A0A]/10"}`}>
                <f.icon className={`h-5 w-5 ${i === 1 ? "text-[#4C8DFF]" : "neon-text"}`} />
              </div>
              <h3 className="font-display text-xl font-bold">{f.title}</h3>
              <p className="text-zinc-400 mt-2 leading-relaxed text-sm">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Privacy + Request */}
      <section id="request" className="max-w-6xl mx-auto px-4 sm:px-6 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] p-8">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="h-5 w-5 text-[#4C8DFF]" />
              <span className="overline">Privacy First</span>
            </div>
            <h2 className="font-display text-3xl font-bold tracking-tight">Your data stays yours.</h2>
            <ul className="mt-6 space-y-4 text-zinc-300">
              {[
                "We do NOT harvest sensitive personal information. Ever.",
                "We only collect a name and email so the commissioner can invite you.",
                "Player accounts are created by their league Commissioner — there is no open player self-registration. Want to run your own league? Use \"Create a League\" from the sign-in page.",
                "No selling data, no ads, no tracking pixels.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <Lock className="h-4 w-4 text-[#4C8DFF] mt-1 shrink-0" />
                  <span className="text-sm leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-[#1F1F22] bg-[#0A0A0A] p-8">
            <span className="overline">Apply for a Spot</span>
            <h2 className="font-display text-3xl font-bold tracking-tight mt-2">Request Access</h2>
            <p className="text-zinc-400 mt-2 text-sm">Submit your details and the commissioner will review your application.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="access-request-form">
              <div>
                <label className="text-xs text-zinc-400 font-semibold">Name</label>
                <input data-testid="access-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#D50A0A] focus:border-[#D50A0A] transition-colors" placeholder="Your name" />
              </div>
              <div>
                <label className="text-xs text-zinc-400 font-semibold">Email</label>
                <input data-testid="access-email-input" required type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#D50A0A] focus:border-[#D50A0A] transition-colors" placeholder="you@email.com" />
              </div>
              <div>
                <label className="text-xs text-zinc-400 font-semibold">Message (optional)</label>
                <textarea data-testid="access-message-input" value={form.message} rows={3}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-1 w-full rounded-lg bg-[#121212] border border-[#1F1F22] px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#D50A0A] focus:border-[#D50A0A] transition-colors resize-none" placeholder="Tell us why you'd be a great addition" />
              </div>
              <button data-testid="access-submit-btn" disabled={submitting}
                className="w-full py-3 rounded-full bg-[#D50A0A] text-white font-bold hover:shadow-[0_0_18px_rgba(213,10,10,0.4)] active:scale-95 transition-transform disabled:opacity-60 flex items-center justify-center gap-2">
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Submit Request
              </button>
            </form>
          </div>
        </div>
        <p className="text-center text-zinc-600 text-xs mt-12">© 2026 NFL Pick House · Live data by Tank01 NFL API</p>
      </section>
    </div>
  );
}
